import React from "react";
import {
  ShoppingCartIcon,
  UsersIcon,
  DollarSignIcon,
  SettingsIcon,
  BuildingIcon,
  MegaphoneIcon,
} from "./navbar-icons";
import type { ModulesSettings } from "./zod/gym";
import { config } from "../config/config";

export interface MenuItem {
  label: string;
  href: string;
}

export interface MenuCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: MenuItem[];
}

export interface SuperAdminMenuItem {
  id: string;
  label: string;
  href: string;
  hasSubItems: boolean;
  icon?: React.ReactNode;
  subItems?: MenuItem[];
}

export const getMenuCategories = (
  modules?: ModulesSettings,
): Omit<MenuCategory, "icon">[] => {
  const enabled = (key: keyof ModulesSettings) =>
    modules ? (modules[key]?.enabled ?? false) : true;

  return [
    {
      id: "operaciones",
      label: "Operaciones",
      items: [
        ...(enabled("accessControl")
          ? [{ label: "Control de Acceso", href: "/access-control" }]
          : []),
        ...(enabled("planSales")
          ? [{ label: "Ventas Planes", href: "/plan-payments" }]
          : []),
        ...(enabled("foodSales")
          ? [{ label: "Ventas Alimentos", href: "/food-sales" }]
          : []),
        ...(enabled("merchandiseSales")
          ? [{ label: "Ventas Productos", href: "/merchandise-sales" }]
          : []),
      ],
    },
    {
      id: "gestion",
      label: "Gestión",
      items: [
        { label: "Clientes", href: "/clients" },
        ...(enabled("planSales")
          ? [{ label: "Suscripciones", href: "/subscriptions" }]
          : []),
        ...(enabled("bookings")
          ? [{ label: "Calendario / Clases", href: "/bookings" }]
          : []),
        ...(enabled("trainers")
          ? [{ label: "Entrenadores", href: "/trainers" }]
          : []),
        ...(enabled("shiftManagement")
          ? [{ label: "Turnos", href: "/shifts" }]
          : []),
        ...(enabled("b2bCrm") ? [{ label: "CRM B2B", href: "/b2b-crm" }] : []),
      ],
    },
    {
      id: "finanzas",
      label: "Finanzas",
      items: [
        ...(enabled("planSales") ||
        enabled("foodSales") ||
        enabled("merchandiseSales") ||
        enabled("otherIncome")
          ? [{ label: "Ingresos", href: "/unified-incomes" }]
          : []),
        ...(enabled("onlinePayments")
          ? [{ label: "Pagos Online", href: "/gateway-transactions" }]
          : []),
        ...(enabled("metrics")
          ? [{ label: "Métricas", href: "/metrics" }]
          : []),
        ...(enabled("salesOrders")
          ? [{ label: "Órdenes de Venta", href: "/sales-orders" }]
          : []),
        ...(enabled("invoicing")
          ? [{ label: "Facturas", href: "/invoices" }]
          : []),
        ...(enabled("productionMetrics")
          ? [{ label: "Métricas B2B", href: "/production-metrics" }]
          : []),
        ...(enabled("expenses")
          ? [{ label: "Gastos", href: "/expenses" }]
          : []),
        ...(enabled("otherIncome")
          ? [{ label: "Otros Ingresos", href: "/other-incomes" }]
          : []),
      ],
    },
    {
      id: "mi-negocio",
      label: "Mi Negocio",
      items: [
        ...(enabled("planSales") ? [{ label: "Planes", href: "/plans" }] : []),
        ...(enabled("foodSales")
          ? [{ label: "Alimentos", href: "/foods" }]
          : []),
        ...(enabled("merchandiseSales")
          ? [{ label: "Productos", href: "/merchandise" }]
          : []),
        ...(enabled("inventory")
          ? [{ label: "Inventario", href: "/inventory" }]
          : []),
        ...(enabled("machinery")
          ? [{ label: "Maquinaria", href: "/machinery" }]
          : []),
        ...(enabled("suppliers")
          ? [{ label: "Proveedores", href: "/suppliers" }]
          : []),
        ...(enabled("productCatalog")
          ? [
              { label: "Materiales", href: "/materials" },
              { label: "Catálogo Productos", href: "/products" },
            ]
          : []),
      ],
    },
    {
      id: "marketing",
      label: "Marketing",
      items: [
        ...(enabled("emailCampaigns")
          ? [{ label: "Campañas", href: "/campaigns" }]
          : []),
        ...(enabled("clientApp")
          ? [{ label: "Banners App", href: "/app-banners" }]
          : []),
      ],
    },
    {
      id: "configuracion",
      label: "Configuración",
      items: [
        { label: "Mi Perfil", href: "/profile" },
        ...(config.isDevelopment
          ? [{ label: "Cobro Dashcore", href: "/billing" }]
          : []),
        { label: "Ayuda", href: "/help" },
        { label: "Admin", href: "/admin" },
        { label: "Monitoreo", href: "/monitoring" },
      ],
    },
  ];
};

// Links del superadmin agrupados en dropdowns con ícono, al estilo del manager.
export const getSuperAdminMenuItems = (
  pendingBanners = 0,
): SuperAdminMenuItem[] => [
  {
    id: "dashboard",
    label: "Dashboard",
    href: "/admin/dashboard",
    hasSubItems: false,
  },
  {
    id: "admin",
    label: "Admin",
    href: "/admin/gyms",
    hasSubItems: true,
    icon: <BuildingIcon className="w-3.5 h-3.5" />,
    subItems: [
      { label: "Negocios", href: "/admin/gyms" },
      { label: "Usuarios", href: "/admin/users" },
      { label: "Alta de negocio", href: "/admin/new-gym-guide" },
      {
        label: pendingBanners
          ? `Banners App (${pendingBanners})`
          : "Banners App",
        href: "/admin/app-banners",
      },
      { label: "Novedades App", href: "/admin/app-releases" },
      { label: "Documentos legales", href: "/admin/legal" },
    ],
  },
  {
    id: "finanzas",
    label: "Finanzas",
    href: "/admin/subscriptions",
    hasSubItems: true,
    icon: <DollarSignIcon className="w-3.5 h-3.5" />,
    subItems: [
      { label: "Suscripciones", href: "/admin/subscriptions" },
      { label: "Historial de ventas", href: "/admin/sales" },
      { label: "Pagos online", href: "/admin/gateway-transactions" },
      { label: "Planes", href: "/admin/plans" },
      { label: "Gastos", href: "/admin/expenses" },
    ],
  },
  {
    id: "configuracion",
    label: "Configuración",
    href: "/monitoring",
    hasSubItems: true,
    icon: <SettingsIcon className="w-3.5 h-3.5" />,
    subItems: [
      { label: "Monitoreo", href: "/monitoring" },
      { label: "Tester de correos", href: "/admin/mail-tester" },
      { label: "Consumo de correos", href: "/admin/mail-usage" },
      { label: "Mi Perfil", href: "/profile" },
      { label: "Ayuda", href: "/help" },
    ],
  },
];

export const getCategoryIcon = (categoryId: string) => {
  const iconMap: Record<string, React.ReactNode> = {
    operaciones: <ShoppingCartIcon className="w-3.5 h-3.5" />,
    gestion: <UsersIcon className="w-3.5 h-3.5" />,
    finanzas: <DollarSignIcon className="w-3.5 h-3.5" />,
    "mi-negocio": <BuildingIcon className="w-3.5 h-3.5" />,
    marketing: <MegaphoneIcon className="w-3.5 h-3.5" />,
    configuracion: <SettingsIcon className="w-3.5 h-3.5" />,
  };
  return iconMap[categoryId];
};
